import { Add, Delete, Edit } from "@mui/icons-material"
import { Stack } from "@mui/material"
import type { GridColDef } from "@mui/x-data-grid-pro"
import { GridActionsCellItem } from "@mui/x-data-grid-pro"
import type { BiomeshContactResponse } from "@phc/common/gen/threatAnalyticsGen"
import { useListContactsBiomeshContactListGet } from "@phc/common/gen/threatAnalyticsGen"
import type { FC } from "react"
import { useCallback, useMemo, useState } from "react"
import { BaseGrid } from "../BaseGrid/BaseGrid"
import Button from "../Shared/Button"
import { ContactFormDialog } from "./ContactFormDialog"
import { DeleteContactDialog } from "./DeleteContactDialog"

export const ContactList: FC = () => {
  const { data: contacts, isLoading } = useListContactsBiomeshContactListGet()

  const [formOpen, setFormOpen] = useState(false)
  const [editContact, setEditContact] = useState<
    BiomeshContactResponse | undefined
  >()
  const [deleteContact, setDeleteContact] = useState<
    BiomeshContactResponse | undefined
  >()

  const handleAdd = useCallback(() => {
    setEditContact(undefined)
    setFormOpen(true)
  }, [])

  const handleEdit = useCallback((contact: BiomeshContactResponse) => {
    setEditContact(contact)
    setFormOpen(true)
  }, [])

  const handleCloseForm = useCallback(() => {
    setFormOpen(false)
    setEditContact(undefined)
  }, [])

  const columns: GridColDef<BiomeshContactResponse>[] = useMemo(
    () => [
      { field: "first_name", headerName: "First Name", flex: 1 },
      { field: "last_name", headerName: "Last Name", flex: 1 },
      { field: "rank", headerName: "Rank", flex: 0.7 },
      { field: "email", headerName: "Email", flex: 1.2 },
      { field: "phone", headerName: "Phone", flex: 0.8 },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 1,
        valueFormatter: (value: string | null) =>
          value ? new Date(value).toLocaleDateString() : "—",
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 100,
        disableExport: true,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="edit"
            icon={<Edit />}
            label="Edit"
            onClick={() => handleEdit(row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<Delete />}
            label="Delete"
            onClick={() => setDeleteContact(row)}
          />,
        ],
      },
    ],
    [handleEdit]
  )

  return (
    <>
      <Stack spacing={2}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-end",
          }}
        >
          <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
            Add Contact
          </Button>
        </Stack>
        <BaseGrid
          rows={contacts ?? []}
          columns={columns}
          getRowId={row => row.contact_id}
          loading={isLoading}
          search
          showToolbar
          exported
          exportFileName="biomesh-contacts"
          placeholder="Search contacts..."
        />
      </Stack>
      <ContactFormDialog
        key={editContact?.contact_id ?? "new"}
        open={formOpen}
        onClose={handleCloseForm}
        contact={editContact}
      />
      {deleteContact && (
        <DeleteContactDialog
          open
          onClose={() => setDeleteContact(undefined)}
          contact={deleteContact}
        />
      )}
    </>
  )
}
